import React, { useEffect, useState } from 'react';

import Label from '@components/label/Label';
import SelectDateInput from '@features/member-management/components/SelectDateInput';
import { AddMemberFormDataType, ValidationError } from '@types';
import { isInvalidEmail } from '@utils/validations';

import Input from '../input/Input';

interface MemberPersonalInfoProps {
  formData: AddMemberFormDataType;
  handleFormDataChange: (
    field: keyof AddMemberFormDataType,
    value: string
  ) => void;
  setError: (error: ValidationError) => void;
  error: ValidationError;
  containerRef: React.RefObject<HTMLElement>;
  onFocused: () => void;
}

const MemberPersonalInfo = ({
  formData,
  handleFormDataChange,
  setError,
  error,
  containerRef,
  onFocused
}: MemberPersonalInfoProps) => {
  const [isLastMonthForDob, setIsLastMonthForDob] = useState(false);

  const { email, dateOfBirth } = formData;

  const onMemberEmailChange = (value: string) => {
    setError({ ...error, email: '' });
    handleFormDataChange('email', value);
  };

  const onEmailBlur = (value: string) => {
    if (value && isInvalidEmail(value)) {
      setError({ ...error, email: 'Invalid email' });
    }
  };

  const onDOBChange = (dob?: Date) => {
    handleFormDataChange('dateOfBirth', dob?.toLocaleDateString('en-CA') ?? '');
  };

  const onClearDOB = () => onDOBChange();

  const getIsLastMonthForDob = (currentDate: Date) => {
    const today = new Date();
    const isLastMonth =
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear();
    setIsLastMonthForDob(isLastMonth);
  };

  useEffect(() => {
    getIsLastMonthForDob(dateOfBirth ? new Date(dateOfBirth) : new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role="presentation"
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={onFocused}
      onClick={onFocused}
      onFocus={onFocused}
      className="grid w-full grid-cols-2 gap-4 rounded-xl bg-theme p-6 shadow-tableShadow"
    >
      <div className="gap-4">
        <Label label="Email" />
        <Input
          value={email}
          handleInputChange={onMemberEmailChange}
          type="text"
          placeholder="Enter email ID"
          error={!!error.email}
          errorMessage={error.email}
          handleInputBlur={onEmailBlur}
        />
      </div>
      <SelectDateInput
        modalTitle="Select Date of Birth"
        label="Date of Birth"
        datePickerLabel="__/__/__"
        handleDateSelect={onDOBChange}
        selectedDate={dateOfBirth}
        isMaxDateRequired
        calendarStyle={`${isLastMonthForDob ? 'disable-next-month' : ''}`}
        onShownDateChange={getIsLastMonthForDob}
        onClearEndDate={onClearDOB}
      />
    </div>
  );
};

export default MemberPersonalInfo;
